(function () {
  'use strict';

  class DrawerManager {
    constructor() {
      this.container = null;
      this.overlay = null;
      this.panels = {};
      this.currentPanel = null;
      this.isOpen = false;
      this.triggerElement = null;

      this.init();
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      this.container = document.querySelector('.drawer-container');
      this.overlay = document.getElementById('product-drawer__overlay');

      if (!this.container) {
        console.error('Drawer container not found');
        return;
      }

      /* Keep drawers above sticky product media stacking contexts */
      if (this.overlay && this.overlay.parentElement !== document.body) {
        document.body.appendChild(this.overlay);
      }
      if (this.container.parentElement !== document.body) {
        document.body.appendChild(this.container);
      }

      this.panels = {
        OPEN_PRODUCTDETAILS: this.container.querySelector('[data-drawer="product-details"]'),
        OPEN_SIZECHART: this.container.querySelector('[data-drawer="sizechart"]'),
        OPEN_DELIVERYRETURNS: this.container.querySelector('[data-drawer="delivery-returns"]'),
        OPEN_HOWTOMEASURE: this.container.querySelector('[data-drawer="how-to-measure"]'),
        OPEN_VARIANTSELECTOR: this.container.querySelector('[data-drawer="variant-selector"]'),
      };

      Object.values(this.panels).forEach((panel) => {
        if (panel) panel.classList.add('hidden');
      });

      this.bindEvents();
      this.sizeConvertor();
    }

    bindEvents() {
      document.querySelectorAll('[data-drawer-trigger]').forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const trigger = button.getAttribute('data-drawer-trigger');
          this.handleTriggerClick(trigger, button);
        });
      });

      document.querySelectorAll('[data-drawer-transition]').forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const transition = button.getAttribute('data-drawer-transition');
          this.handleTransition(transition);
        });
      });

      document.querySelectorAll('[data-drawer-close]').forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeDrawer();
        });
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.closeDrawer());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.closeDrawer();
        }
      });
    }

    handleTriggerClick(trigger, button) {
      this.triggerElement = button;

      if (!this.isOpen) {
        this.openDrawer(trigger);
      } else {
        this.switchPanel(trigger);
      }
    }

    handleTransition(transition) {
      if (transition === 'CLOSE_DRAWER') {
        this.closeDrawer();
      } else {
        this.switchPanel(transition);
      }
    }

    // Helper function to pause videos in a panel
    pauseVideosInPanel(panel) {
      if (!panel) return;

      // Find all video elements within the panel
      const videos = panel.querySelectorAll('video');
      videos.forEach((video) => {
        if (!video.paused) {
          video.pause();
          console.log('Video paused in panel');
        }
      });

      // Also handle iframe videos (like YouTube)
      const iframes = panel.querySelectorAll('iframe');
      iframes.forEach((iframe) => {
        // For YouTube iframes, send a postMessage to pause
        if (iframe.src.includes('youtube.com') || iframe.src.includes('youtu.be')) {
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        }
        // For Vimeo iframes
        else if (iframe.src.includes('vimeo.com')) {
          iframe.contentWindow.postMessage('{"method":"pause"}', '*');
        }
      });
    }

    openDrawer(panelKey) {
      const panel = this.panels[panelKey];
      if (!panel) return;

      this.container.classList.add('active');
      if (this.overlay) {
        this.overlay.classList.add('active');
      }
      document.body.classList.add('drawer-open');

      Object.values(this.panels).forEach((p) => {
        if (p) {
          p.classList.add('hidden');
          p.setAttribute('aria-hidden', 'true');
          // Pause videos when hiding panels
          this.pauseVideosInPanel(p);
        }
      });

      setTimeout(() => {
        panel.classList.remove('hidden');
        panel.setAttribute('aria-hidden', 'false');
        this.currentPanel = panelKey;
        this.isOpen = true;

        const firstFocusable = panel.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (firstFocusable) firstFocusable.focus();
      });
    }

    switchPanel(panelKey) {
      const panel = this.panels[panelKey];
      if (!panel || this.currentPanel === panelKey) return;

      const currentPanelElement = this.panels[this.currentPanel];

      if (currentPanelElement) {
        currentPanelElement.classList.add('hidden');
        currentPanelElement.setAttribute('aria-hidden', 'true');
        // Pause videos when hiding the current panel
        this.pauseVideosInPanel(currentPanelElement);
      }

      setTimeout(() => {
        panel.classList.remove('hidden');
        panel.setAttribute('aria-hidden', 'false');
        this.currentPanel = panelKey;

        const firstFocusable = panel.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (firstFocusable) firstFocusable.focus();
      });
    }

    closeDrawer() {
      if (!this.isOpen) return;

      if (this.triggerElement) {
        this.triggerElement.focus();
      }

      if (this.currentPanel && this.panels[this.currentPanel]) {
        // Pause videos before closing
        this.pauseVideosInPanel(this.panels[this.currentPanel]);

        this.panels[this.currentPanel].classList.add('hidden');
        this.panels[this.currentPanel].setAttribute('aria-hidden', 'true');
      }

      this.container.classList.remove('active');
      if (this.overlay) {
        this.overlay.classList.remove('active');
      }
      document.body.classList.remove('drawer-open');

      this.isOpen = false;
      this.currentPanel = null;
      this.triggerElement = null;
    }

    sizeConvertor() {
      let ele = this.container.querySelector('.size-tab-navigation');
      ele?.addEventListener('change', (e) => {
        let chartContainer = document.querySelector('#variant-drawer-content');
        if (!chartContainer) return;
        if (chartContainer.classList.contains('size-converted')) {
          chartContainer.classList.remove('size-converted');
        } else {
          chartContainer.classList.add('size-converted');
        }
      });
    }
  }

  window.drawerManager = new DrawerManager();

  window.openDrawer = function (panelKey) {
    if (window.drawerManager) {
      window.drawerManager.openDrawer(panelKey);
    }
  };

  window.closeDrawer = function () {
    if (window.drawerManager) {
      window.drawerManager.closeDrawer();
    }
  };
})();

document.querySelector('#variant-drawer-content')?.addEventListener('change', (e) => {
  if (!e.target.matches('input[type="radio"][name="drawer_variant_id"]')) return;

  const price = e.target.dataset.variantPrice;
  const comparePrice = e.target.dataset.variantComparePrice;
  const variantId = e.target.dataset.variantId;
  const productUrl = e.target.dataset.productUrl;

  const productCodeValue = e.target.dataset.variantProductCode;
  const weight = e.target.dataset.variantWeight;
  const drawerProductInfo = document.querySelector('product-info[data-section]');
  const sectionId = drawerProductInfo?.dataset.section;
  const associatedForm = sectionId ? document.getElementById(`product-form-${sectionId}`) : null;
  const formVariantInput = associatedForm?.querySelector('input.product-variant-id[name="id"]');

  if (formVariantInput && variantId) {
    formVariantInput.value = variantId;
    formVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Update sale price (indian-price / money filters return HTML)
  document.querySelectorAll('.price-item.price-item--sale.price-item--last').forEach((ele) => {
    ele.innerHTML = price;
  });

  // Update regular price (shown when no sale)
  document.querySelectorAll('.price__regular .price-item.price-item--regular').forEach((ele) => {
    ele.innerHTML = price;
  });

  // Update compare-at price and sale/no-sale state
  const priceContainer = document.querySelector('.price');
  if (priceContainer) {
    if (comparePrice) {
      priceContainer.classList.add('price--on-sale');
      document.querySelectorAll('.price__sale .price-item.price-item--regular').forEach((ele) => {
        ele.innerHTML = comparePrice;
      });
    } else {
      priceContainer.classList.remove('price--on-sale');
    }
  }

  // Update drawer product code
  const drawerProductCode = document.getElementById('drawerProductCode');
  if (drawerProductCode) {
    if (productCodeValue) {
      drawerProductCode.classList.remove('hidden');
      const productCodeEl = document.getElementById('productCode');
      if (productCodeEl) productCodeEl.innerText = productCodeValue;
    } else {
      drawerProductCode.classList.add('hidden');
    }
  }

  // Update drawer weight
  const drawerWeight = document.getElementById('drawerWeight');
  if (drawerWeight) {
    if (weight) {
      drawerWeight.classList.remove('hidden');
      const weightValue = document.getElementById('drawerWeightValue');
      if (weightValue) weightValue.innerText = weight;
    } else {
      drawerWeight.classList.add('hidden');
    }
  }

  // Update URL with variant ID
  if (variantId && productUrl) {
    window.history.replaceState({}, '', productUrl + '?variant=' + variantId);
  }

  // Fetch updated section HTML to update description, product code on main page
  const productInfo = document.querySelector('product-info[data-section]');
  if (productInfo && productUrl) {
    const sectionId = productInfo.dataset.section;
    fetch(`${productUrl}?variant=${variantId}&section_id=${sectionId}`)
      .then((res) => res.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');

        // Update description
        const descSource = html.getElementById(`Description-${sectionId}`);
        const descDest = document.getElementById(`Description-${sectionId}`);
        if (descSource && descDest) {
          descDest.innerHTML = descSource.innerHTML;
        }

        // Update drawer description
        const drawerDescription = document.getElementById('drawerDescription');
        const drawerDescriptionValue = document.getElementById('drawerDescriptionValue');
        if (drawerDescription && descSource) {
          const descriptionHtml = descSource.innerHTML.trim();
          if (descriptionHtml) {
            drawerDescription.classList.remove('hidden');
            if (drawerDescriptionValue) {
              drawerDescriptionValue.innerHTML = descriptionHtml;
            } else {
              drawerDescription.innerHTML = descriptionHtml;
            }
          } else {
            drawerDescription.classList.add('hidden');
          }
        }

        // Update product code on main page
        const codeSource = html.getElementById(`ProductCode-${sectionId}`);
        const codeDest = document.getElementById(`ProductCode-${sectionId}`);
        if (codeSource && codeDest) {
          codeDest.innerHTML = codeSource.innerHTML;
        }

        // Update inventory status on main page
        const invSource = html.getElementById(`InventoryStatus-${sectionId}`);
        const invDest = document.getElementById(`InventoryStatus-${sectionId}`);
        if (invSource && invDest) {
          invDest.innerHTML = invSource.innerHTML;
        }

        // Re-bind copy button after DOM replacement
        bindCopyClipboard();
      });
  }
});

document.getElementById('SecondaryImage')?.addEventListener('click', function (event) {
  event.preventDefault();

  const mediaId = event.currentTarget.getAttribute('data-media-handle');
  console.log('Media ID:', mediaId);

  const mediaButton = document.querySelector(`[data-media-name='${mediaId}']`);
  if (!mediaButton) return;

  window.smoothScrollTo(document.scrollingElement || document.documentElement, mediaButton.offsetTop + 54, 0);
});

window.smoothScrollTo = (element, targetPosition, duration, property = 'scrollTop') => {
  console.log(targetPosition);
  const start = element[property];
  const startTime = performance.now();

  function animateScroll(currentTime) {
    const timeElapsed = currentTime - startTime;
    const progress = Math.min(timeElapsed / duration, 1); // Progress is between 0 and 1
    const easeInOutQuad = (t) => {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    };
    const easeProgress = easeInOutQuad(progress);
    element[property] = start + (targetPosition - start) * easeProgress;

    if (progress < 1) {
      requestAnimationFrame(animateScroll);
    }
  }

  requestAnimationFrame(animateScroll);
};

function bindCopyClipboard() {
  document.querySelectorAll('#copyClipboard').forEach((btn) => {
    btn.replaceWith(btn.cloneNode(true));
  });
  document.querySelectorAll('#copyClipboard').forEach((btn) => {
    btn.addEventListener('click', () => {
      const container = btn.closest('.product-code__text, .drawer-text, .product-code, li');
      const codeEl = container?.querySelector('#productCode, #ProductCodeSS, .product-code__value');
      if (!codeEl) return;

      navigator.clipboard
        .writeText(codeEl.innerText.trim())
        .then(() => {
          showMessage('Copied!');
        })
        .catch(() => {
          showMessage('Failed to copy');
        });
    });
  });
}

bindCopyClipboard();

const showMessage = (() => {
  let timeoutId;
  return function (message) {
    let ele = document.querySelector('#copyMessage');
    ele.innerText = message;
    ele.classList.add('show');

    if (timeoutId) clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      ele.classList.remove('show');
    }, 2000);
  };
})();
